import { useMemo } from 'react'

import {
  IntegrationsSyncRunOriginEnum,
  MarketplaceDbIntegration,
} from 'generated/resources/datatable.graphql'
import { ReadSyncValidationsQuery } from 'generated/resources/graphql'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

export type SyncIssue = {
  title: string
  description: string
  buttonLabel?: string
  buttonUrl?: string
  onAction?: () => void
  affectedNames?: string[]
}

type SyncValidationResult = NonNullable<
  ReadSyncValidationsQuery['integrations']['syncValidationsConnection']['nodes'][0]
>['issues'][0]

export const useCompensationsSyncIssues = (
  validationResults: SyncValidationResult[],
  marketplaceIntegration: MarketplaceDbIntegration | null,
  isValidation: boolean = true,
  onReviewMissingAmounts?: () => void,
  companyId?: string,
  origin?: IntegrationsSyncRunOriginEnum
): SyncIssue[] => {
  const { navigation } = useNavigation()
  const switchParam = companyId ? `?switchToCompanyId=${companyId}` : ''
  const integrationSettingsUrl = `${navigation.discover.marketplace.item.settings.fullPath({
    id: marketplaceIntegration?.datoId ?? '0',
  })}${switchParam}`

  const employeesUrl = `${navigation.employees.fullPath()}${switchParam}`

  const issuesByKey: Record<string, SyncIssue> = {
    authentication_failed: {
      title: i18n.t(
        'compensations.integrations.sync_validations.blocking_issues.authentication_failed.title',
        {
          integration_name: marketplaceIntegration?.displayName,
        }
      ),
      description: i18n.t(
        'compensations.integrations.sync_validations.blocking_issues.authentication_failed.description',
        {
          integration_name: marketplaceIntegration?.displayName,
        }
      ),
      buttonLabel: i18n.t(
        'compensations.integrations.sync_validations.blocking_issues.authentication_failed.button_label'
      ),
      buttonUrl: integrationSettingsUrl,
    },
    missing_legal_entity_code: {
      title: i18n.t(
        'compensations.integrations.sync_validations.blocking_issues.missing_legal_entity_code.title',
        {
          integration_name: marketplaceIntegration?.displayName,
        }
      ),
      description: i18n.t(
        'compensations.integrations.sync_validations.blocking_issues.missing_legal_entity_code.description',
        {
          integration_name: marketplaceIntegration?.displayName,
        }
      ),
      buttonLabel: i18n.t(
        'compensations.integrations.sync_validations.blocking_issues.missing_legal_entity_code.button_label'
      ),
      buttonUrl: integrationSettingsUrl,
    },
    missing_payroll_concept_code: {
      title: i18n.t(
        'compensations.integrations.sync_validations.blocking_issues.missing_payroll_concept_code.title',
        {
          integration_name: marketplaceIntegration?.displayName,
        }
      ),
      description: i18n.t(
        'compensations.integrations.sync_validations.blocking_issues.missing_payroll_concept_code.description',
        {
          integration_name: marketplaceIntegration?.displayName,
        }
      ),
      buttonLabel: i18n.t(
        'compensations.integrations.sync_validations.blocking_issues.missing_payroll_concept_code.button_label'
      ),
      buttonUrl: integrationSettingsUrl,
    },
    missing_employee_code: {
      title: i18n.t(
        'compensations.integrations.sync_validations.blocking_issues.missing_employee_code.title',
        {
          integration_name: marketplaceIntegration?.displayName,
        }
      ),
      description: i18n.t(
        'compensations.integrations.sync_validations.blocking_issues.missing_employee_code.description',
        {
          integration_name: marketplaceIntegration?.displayName,
        }
      ),
      buttonLabel: i18n.t(
        'compensations.integrations.sync_validations.blocking_issues.missing_employee_code.button_label'
      ),
      buttonUrl: employeesUrl,
    },
    missing_employee_company_identifier: {
      title: i18n.t(
        'compensations.integrations.sync_validations.blocking_issues.missing_employee_company_identifier.title',
        {
          integration_name: marketplaceIntegration?.displayName,
        }
      ),
      description: i18n.t(
        'compensations.integrations.sync_validations.blocking_issues.missing_employee_company_identifier.description',
        {
          integration_name: marketplaceIntegration?.displayName,
        }
      ),
      buttonLabel: i18n.t(
        'compensations.integrations.sync_validations.blocking_issues.missing_employee_company_identifier.button_label'
      ),
      buttonUrl: employeesUrl,
    },
    missing_amount: {
      title: i18n.t(
        'compensations.integrations.sync_validations.blocking_issues.missing_amount.title'
      ),
      description: i18n.t(
        'compensations.integrations.sync_validations.blocking_issues.missing_amount.description'
      ),
      buttonLabel: i18n.t(
        'compensations.integrations.sync_validations.blocking_issues.missing_amount.button_label'
      ),
      buttonUrl:
        origin === 'professionalscompensations'
          ? `${navigation.compensations.cycles.list.fullPath()}${switchParam}`
          : undefined,
      onAction: origin === 'professionalscompensations' ? undefined : onReviewMissingAmounts,
    },
  }

  const validationResultIssues: SyncIssue[] = []
  const syncResultIssues: SyncIssue[] = []

  for (const result of validationResults) {
    const issue = issuesByKey[result.errorMessage.key]
    if (isValidation && !issue) continue
    const title = i18n.t(
      'bookkeepers.clients.payroll_cycles.sync_status_stats.result_issues.api_error.title',
      {
        integration_name: marketplaceIntegration?.displayName,
      }
    )
    const description =
      result.errorMessage.value ||
      i18n.t('bookkeepers.clients.payroll_cycles.sync_status_stats.result_issues.unknown_error')

    const entry: SyncIssue = {
      title: issue?.title ?? title,
      description: issue?.description ?? description,
      buttonLabel: issue?.buttonLabel,
      buttonUrl: issue?.buttonUrl,
      onAction: issue?.onAction,
      affectedNames: result.affectedNames,
    }

    if (issue) {
      validationResultIssues.push(entry)
    } else {
      syncResultIssues.push(entry)
    }
  }

  return [...validationResultIssues, ...syncResultIssues]
}

export function useIssuesWithModalClose(issues: SyncIssue[], closeModal: () => void) {
  return useMemo(
    () =>
      issues.map((issue) => {
        if (!issue.onAction) return issue
        const { onAction } = issue
        return {
          ...issue,
          onAction: () => {
            onAction()
            closeModal()
          },
        }
      }),
    [issues, closeModal]
  )
}
